import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Download, Info, Moon, Sun, FileText, Type, Upload } from "lucide-react";
import { jsPDF } from "jspdf";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

const PAGE_SIZES = {
  "a4": { name: "A4", width: 210, height: 297 },
  "letter": { name: "Letter", width: 215.9, height: 279.4 },
  "a5": { name: "A5", width: 148, height: 210 },
};

const FONT_SIZES = {
  "10": "Small (10pt)",
  "12": "Normal (12pt)",
  "14": "Large (14pt)",
  "16": "Extra Large (16pt)",
};

function TextToPdfNew() {
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [pageSize, setPageSize] = useState("a4");
  const [fontSize, setFontSize] = useState("12");
  const [orientation, setOrientation] = useState("portrait");
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Text to PDF - Convert Text to PDF Document - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setText(event.target?.result || "");
        setTitle(file.name.replace(/\.[^/.]+$/, ''));
        toast.success("File loaded successfully");
      };
      reader.readAsText(file);
    }
  };

  const convertToPdf = () => {
    if (!text.trim()) {
      toast.error("Please enter some text");
      return;
    }

    try {
      const page = PAGE_SIZES[pageSize];
      const isLandscape = orientation === "landscape";
      const pageWidth = isLandscape ? page.height : page.width;
      const pageHeight = isLandscape ? page.width : page.height;
      
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight]
      });

      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      const lineHeight = parseInt(fontSize) * 0.5;
      
      pdf.setFontSize(parseInt(fontSize));
      
      // Add title if provided
      let yPosition = margin;
      if (title) {
        pdf.setFontSize(parseInt(fontSize) + 4);
        pdf.setFont(undefined, 'bold');
        pdf.text(title, margin, yPosition);
        yPosition += lineHeight * 2;
        pdf.setFontSize(parseInt(fontSize));
        pdf.setFont(undefined, 'normal');
      }

      // Split text into lines
      const lines = pdf.splitTextToSize(text, maxWidth);
      
      for (let i = 0; i < lines.length; i++) {
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.text(lines[i], margin, yPosition);
        yPosition += lineHeight;
      }

      const fileName = title ? `${title}.pdf` : `text_document_${Date.now()}.pdf`;
      pdf.save(fileName);
      toast.success("PDF created successfully!");
    } catch (error) {
      console.error("PDF creation error:", error);
      toast.error("Failed to create PDF");
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-emerald-50 to-teal-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Type className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full mb-4">
            <Type className="w-5 h-5" />
            <span className="font-medium">Text to PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert Text to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Type or paste text, then convert it to a professional PDF document.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Settings */}
          <Card className="dark:bg-slate-800">
            <CardContent className="p-6 space-y-4">
              <h3 className="font-semibold text-slate-800 dark:text-white">Settings</h3>
              
              <div>
                <Label className="text-sm mb-2 block">Document Title (optional)</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="My Document"
                />
              </div>

              <div>
                <Label className="text-sm mb-2 block">Page Size</Label>
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAGE_SIZES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm mb-2 block">Orientation</Label>
                <div className="flex gap-2">
                  <Button
                    variant={orientation === "portrait" ? "default" : "outline"}
                    onClick={() => setOrientation("portrait")}
                    className={`flex-1 ${orientation === "portrait" ? "bg-emerald-600" : ""}`}
                    size="sm"
                  >
                    Portrait
                  </Button>
                  <Button
                    variant={orientation === "landscape" ? "default" : "outline"}
                    onClick={() => setOrientation("landscape")}
                    className={`flex-1 ${orientation === "landscape" ? "bg-emerald-600" : ""}`}
                    size="sm"
                  >
                    Landscape
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-sm mb-2 block">Font Size</Label>
                <Select value={fontSize} onValueChange={setFontSize}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FONT_SIZES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t">
                <Label className="text-sm mb-2 block">Or upload a .txt file</Label>
                <Input
                  type="file"
                  accept=".txt,text/plain"
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                />
              </div>
            </CardContent>
          </Card>

          {/* Text Editor */}
          <Card className="lg:col-span-2 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 dark:text-white">Your Text</h3>
                <span className="text-sm text-slate-500">{text.length} characters</span>
              </div>
              
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste your text here..."
                className="min-h-[400px] font-mono text-sm resize-none"
              />

              <Button 
                onClick={convertToPdf} 
                className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:opacity-90"
                disabled={!text.trim()}
              >
                <FileText className="w-4 h-4 mr-2" />
                Convert to PDF
              </Button>
            </CardContent>
          </Card>
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default TextToPdfNew;
